(() => {
  const widgetSelector = '.jdgm-review-widget';
  const fallbackTimers = new WeakMap();
  const dynamicWidgets = new Set();
  let initializeTimer;

  function getWidgets(scope = document) {
    const widgets = [];

    if (scope.matches?.(widgetSelector)) widgets.push(scope);
    scope.querySelectorAll?.(widgetSelector).forEach((widget) => widgets.push(widget));

    return widgets;
  }

  function expectedReviewCount(widget) {
    const total = Number.parseInt(widget.dataset.numberOfReviews || '0', 10);
    const pagination = widget.querySelector('.jdgm-paginate[data-per-page]');
    const perPage = Number.parseInt(pagination?.dataset.perPage || '5', 10);

    if (!total) return 0;
    return Math.min(total, perPage || 5);
  }

  function isWidgetComplete(widget) {
    if (!widget?.isConnected) return false;

    const legacyContent = widget.querySelector('.jdgm-legacy-widget-content');
    const reviewWidget = widget.querySelector('.jdgm-rev-widg');
    const expected = expectedReviewCount(widget);
    const rendered = widget.querySelectorAll('.jdgm-rev').length;

    return widget.getBoundingClientRect().height > 100
      && (!legacyContent || getComputedStyle(legacyContent).display !== 'none')
      && (!reviewWidget || getComputedStyle(reviewWidget).display !== 'none')
      && (!expected || rendered >= expected);
  }

  function revealServerRenderedWidget(widget) {
    if (!widget?.isConnected || isWidgetComplete(widget)) return;

    const legacyContent = widget.querySelector('.jdgm-legacy-widget-content');
    const reviewWidget = legacyContent?.querySelector('.jdgm-rev-widg');
    if (!legacyContent || !reviewWidget) return;

    widget.querySelectorAll('.jdgm-temp-hiding-style').forEach((style) => style.remove());
    widget.style.removeProperty('display');
    legacyContent.style.display = 'block';
    reviewWidget.style.display = 'block';
    widget.classList.add('jdgm-review-widget--legacy-fallback');
  }

  function scheduleFallback(widget) {
    if (fallbackTimers.has(widget) || isWidgetComplete(widget)) return;

    const timer = window.setTimeout(() => {
      if (isWidgetComplete(widget)) {
        fallbackTimers.delete(widget);
        return;
      }

      // By this point loader.js has had time to finish. A single late retry is
      // safe and repairs widgets that were skipped during an app/page-builder
      // render without competing with Judge.me's initial page setup.
      try {
        window.jdgm?.initializeWidgets?.();
      } catch (error) {
        console.warn('[Judge.me] Delayed widget initialization failed.', error);
      }

      const revealTimer = window.setTimeout(() => {
        fallbackTimers.delete(widget);
        revealServerRenderedWidget(widget);
      }, 2000);

      fallbackTimers.set(widget, revealTimer);
    }, 6000);

    fallbackTimers.set(widget, timer);
  }

  function initializeDynamicWidgets() {
    window.clearTimeout(initializeTimer);
    initializeTimer = window.setTimeout(() => {
      if (!dynamicWidgets.size) return;

      try {
        window.jdgm?.initializeWidgets?.();
      } catch (error) {
        console.warn('[Judge.me] Dynamic widget initialization failed.', error);
      }

      dynamicWidgets.forEach(scheduleFallback);
      dynamicWidgets.clear();
    }, 150);
  }

  function registerDynamicWidgets(scope) {
    getWidgets(scope).forEach((widget) => dynamicWidgets.add(widget));
    initializeDynamicWidgets();
  }

  function start() {
    // Judge.me owns the initial page render. Calling its global initializer while
    // loader.js is still running can leave the review grid only partly arranged.
    getWidgets(document).forEach(scheduleFallback);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) registerDynamicWidgets(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    registerDynamicWidgets(event.target);
  });
})();
